/**
 * PWA Utilities
 *
 * Utility functions for Progressive Web App functionality:
 * - Online/offline status detection
 * - PWA install prompt handling
 */

// Type definitions
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/**
 * Check if the app is currently online
 */
export function isOnline(): boolean {
  return navigator.onLine
}

/**
 * Subscribe to online/offline status changes
 * @returns Cleanup function to remove event listeners
 */
export function subscribeToOnlineStatus(
  callback: (isOnline: boolean) => void
): () => void {
  const handleOnline = () => callback(true)
  const handleOffline = () => callback(false)

  window.addEventListener('online', handleOnline)
  window.addEventListener('offline', handleOffline)

  return () => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  }
}

/**
 * Prompt the user to install the PWA
 */
export async function promptInstall(
  event: BeforeInstallPromptEvent | null
): Promise<boolean> {
  if (!event) {
    return false
  }

  try {
    await event.prompt()
    const { outcome } = await event.userChoice
    return outcome === 'accepted'
  } catch (error) {
    console.error('PWA install prompt failed:', error)
    return false
  }
}
